import useTranslation from '../useTranslation';
import type { SiteWithParcelAndClientResponse } from 'generatedTypes/client/models';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { Props as DetailsViewEntityLinkProps } from 'components/DetailsViewEntityLink/types';
import type { DetailsViewEntityMenuItem } from 'components/DetailsViewEntityMenu/types';
import type { BoundaryPartData, Geometry } from 'components/Map/types';

import useApplications from 'hooks/useApplications';
import type { ApplicationResponse } from 'hooks/useApplications/types';
import useBoundaries from 'hooks/useBoundaries';
import type { BoundaryResponse } from 'hooks/useBoundaries/types';
import useBoundary from 'hooks/useBoundary';
import useField from 'hooks/useField';
import usePageRoutes from 'hooks/usePageRoutes';
import type { PaginatedGenerator } from 'hooks/usePagination/types';

import { DETAIL_TYPE_CLIENT, DETAIL_TYPE_FARM } from 'pages/FieldDetailsPage/constants';

const useData = () => {
    const { fieldId } = useParams();
    const { EDIT_FIELD } = useTranslation();
    const { fetchField, isLoading } = useField();
    const { boundaryDetailsPage, clientDetailsPage, farmDetailsPage, fieldsPage } = usePageRoutes();
    const {
        applications,
        fetch: fetchApplications,
        isLoading: isApplicationsLoading
    } = useApplications();
    const { fetchBoundary } = useBoundary();
    const navigate = useNavigate();
    const [currentField, setCurrentField] = useState<SiteWithParcelAndClientResponse | undefined>(
        undefined
    );
    const [isApplicationsOpen, setIsApplicationsOpen] = useState(false);
    const [isLinkedEntitiesOpen, setIsLinkedEntitiesOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [selectedApplicationId, setSelectedApplicationId] = useState<string | undefined>(
        undefined
    );
    const [selectedBoundaryGeometry, setSelectedBoundaryGeometry] = useState<Geometry | undefined>(
        undefined
    );
    const isFirstLoad = useRef(true);
    const {
        boundaries,
        fetch: fetchBoundaries,
        isLoading: isBoundariesLoading,
        totalCount: totalBoundariesCount
    } = useBoundaries();
    const boundariesGenerator = useRef<PaginatedGenerator<BoundaryResponse> | null>(null);

    const entitiesLink: DetailsViewEntityLinkProps[] = [
        {
            entityName: currentField?.clientName ?? '',
            entityRoute: clientDetailsPage(currentField?.clientId ?? ''),
            entityType: DETAIL_TYPE_CLIENT
        },
        {
            entityName: currentField?.parcelName ?? '',
            entityRoute: farmDetailsPage(currentField?.parcelId ?? ''),
            entityType: DETAIL_TYPE_FARM
        }
    ];

    const boundaryMenuItems: DetailsViewEntityMenuItem[] = (boundaries ?? []).map(
        ({ id = '', name = '' }) => ({
            entityId: id,
            entityName: name,
            entityRoute: boundaryDetailsPage(id)
        })
    );

    const getCurrentField = async () => {
        if (fieldId) {
            const field = await fetchField({ id: fieldId });

            boundariesGenerator.current = fetchBoundaries({ siteId: fieldId });
            await boundariesGenerator.current.next();

            setCurrentField(field ?? undefined);
            fetchApplications({ siteId: fieldId }).next();
        }
    };

    useEffect(() => {
        if (fieldId) {
            getCurrentField();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldId]);

    const handleSelectApplication = async (application: ApplicationResponse) => {
        setSelectedApplicationId(application.id);

        if (!application.boundaryId) {
            setSelectedBoundaryGeometry(undefined);

            return;
        }

        const boundary = await fetchBoundary({ id: application.boundaryId });

        setSelectedBoundaryGeometry(
            boundary
                ? { isEditable: false, parts: boundary.boundaryParts as BoundaryPartData[] }
                : undefined
        );
    };

    useEffect(() => {
        if (applications && applications.length > 0 && isFirstLoad.current) {
            isFirstLoad.current = false;
            handleSelectApplication(applications[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applications]);

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleToggleApplications = () => {
        setIsApplicationsOpen(previous => !previous);
    };

    const handleToggleLinkedEntities = () => {
        setIsLinkedEntitiesOpen(previous => !previous);
    };

    const handleEditClose = () => {
        if (isEditing) {
            setIsEditing(false);
            setIsClosing(false);
            getCurrentField();
        }
    };

    const dropdownMenuItems = [
        {
            onClick: handleEditClick,
            text: EDIT_FIELD
        }
    ];

    const handleNavigateBack = () => {
        navigate(fieldsPage);
    };

    const handleClose = () => {
        if (isEditing) {
            setIsClosing(true);
        } else {
            handleNavigateBack();
        }
    };

    const handleBoundaryNext = async () => {
        if (!boundariesGenerator.current) {
            return { done: true };
        }

        return boundariesGenerator.current.next();
    };

    return {
        applications,
        boundaryMenuItems,
        currentField,
        dropdownMenuItems,
        entitiesLink,
        handleBoundaryNext,
        handleClose,
        handleEditClose,
        handleSelectApplication,
        handleToggleApplications,
        handleToggleLinkedEntities,
        isApplicationsLoading,
        isApplicationsOpen,
        isBoundariesLoading,
        isClosing,
        isEditing,
        isLinkedEntitiesOpen,
        isLoading,
        selectedApplicationId,
        selectedBoundaryGeometry,
        setIsClosing,
        totalBoundariesCount
    };
};

export default useData;
